<template>
    <div class="tw:w-full">
        <header class="modal-card-head">
            <p class="modal-card-title">{{ capitalize(type) }} Attempt</p>
        </header>

        <section class="modal-card-body">
            <div class="content">
                <div v-if="type === 'quiz'">
                    <h4>Policy Regarding Academic Integrity</h4>

                    <p>
                        Any student who engages in academic dishonesty, including, but not limited to, plagiarizing
                        another person’s work; cheating on an examination or assignment; distributing copies of
                        examinations, assignments, or answer sheets to other students; passing off another’s work as
                        one’s own; and/or aiding one or more other students in committing the same or similar acts of
                        academic dishonesty will be given a grade of zero for the exam or assignment in which the
                        infraction occurred. If a student commits a second act of academic dishonesty, he or she will be
                        dismissed from the university. All acts of academic dishonesty will be recorded on a Notice of
                        Deficiency form and placed in the student’s permanent academic file. A student may appeal
                        his/her dismissal from the university for academic dishonesty. The appeal must be made in
                        writing to the Office of Instruction.
                    </p>

                    <p>
                        By clicking on <strong>"Begin Quiz Attempt"</strong>, I acknowledge that I have read and agree
                        to this policy.
                    </p>

                    <p class="m-b-md">
                        Are you sure you are ready to begin your
                        {{ promptLabel() }}?
                    </p>
                </div>

                <div>
                    <p>
                        After you begin, you will have
                        <span class="has-text-weight-bold">
                            {{ attemptsRemainingAfter }}
                            {{ translate('attempt', attemptsRemainingAfter) }}
                            remaining</span>.
                    </p>

                    <e-field v-if="passwordEnabled" label="Password *">
                        <e-input
                            v-model="userInput"
                            type="password"
                            placeholder="Your password"
                            required
                            password-reveal
                        />
                    </e-field>
                </div>
            </div>
        </section>

        <footer class="modal-card-foot">
            <div class="tw:flex tw:justify-between tw:w-full">
                <e-button label="Close" @click="$emit('close')" @keyup="$emit('close')" />
                <e-button
                    :disabled="disabled"
                    :label="`Begin ${capitalize(type)} Attempt`"
                    type="is-primary"
                    @click="startQuiz"
                    @keyup="startQuiz"
                />
            </div>
        </footer>
    </div>
</template>

<script>
import capitalize from 'lodash/capitalize.js';
import { useI18n } from 'vue-i18n';
import { mapState } from 'vuex';

import AttemptAuthorizationMixin from '@/mixins/AttemptAuthorization.mixin.js';
import QuizAttempt from '@/models/QuizAttempt.js';
import axios from '@/plugins/axios.js';
import QueryBuilder from '@/utilities/QueryBuilder.js';

export default {
    setup() {
        const { t: translate } = useI18n();
        return { translate };
    },
    name: 'LaunchQuiz',

    mixins: [AttemptAuthorizationMixin],

    emits: ['close'],

    props: {
        uuid: {
            type: String,
            required: true,
        },

        type: {
            type: String,
            default: null,
        },

        activity: {
            type: Object,
            required: true,
        },

        preview: {
            type: Boolean,
            default: false,
        },

        submissionCount: {
            type: Number,
            default: 0,
        },
    },

    data: () => ({
        userInput: null,
    }),

    computed: {
        ...mapState('Auth', { authUser: state => state.user }),

        activityContent() {
            return this.getActivityField('activity_content');
        },

        disabled() {
            return this.passwordEnabled ? !this.userInput : false;
        },

        password() {
            return this.activityContent.password;
        },

        passwordEnabled() {
            return this.activityContent.password_enabled;
        },

        currentAttempts() {
            if (this.preview) {
                return this.get('quiz_attempts').filter(preview => !preview.is_preview).length;
            }

            return this.get('quiz_attempts').length || 0;
        },

        maxAttempts() {
            return this.getActivityField('activity_settings').max_attempts_allowed;
        },

        attemptsRemainingAfter() {
            if (this.userHasAuthorizedAttempt) {
                return this.userUnusedAuthorizedAttempts.length - 1;
            }

            if (this.usedAttemptAuthorizations.length > 0) {
                return 0;
            }

            return Math.max(0, this.maxAttempts - this.submissionCount - 1);
        },

        quizId() {
            return this.activityContent.quiz_version_id;
        },

        quizActivityId() {
            return this.activityContent.id;
        },
    },

    created() {},

    methods: {
        capitalize,

        get(field) {
            if (!this.$store.getters[`QuizAttempt/${this.quizId}/get`]) {
                return field === 'title' ? '' : {};
            }

            return this.$store.getters[`QuizAttempt/${this.quizId}/get`](field);
        },

        getActivityField(field) {
            return this.$store.getters[`Activities/${this.uuid}/get`](field);
        },

        syncActivitySubmissionCount() {
            if (!this.uuid) {
                return;
            }

            this.$store.commit('Activities/EDIT', {
                uuid: this.uuid,
                field: 'user_submission_count',
                value: this.submissionCount + 1,
            });
        },

        promptLabel(header = false) {
            const text = this.type.includes('ur') ? 'Survey' : 'Quiz Attempt';

            if (header) return `Begin ${text}`;

            return text;
        },

        async startQuiz() {
            const loading = this.$loading.open({});

            const queryBuilder = new QueryBuilder('quiz-attempts');

            const isPreview = !!this.preview;

            await axios
                .post(queryBuilder.toUrl(), {
                    [`${this.type}_activity_id`]: this.quizActivityId,
                    quiz_version_id: this.quizId,
                    password: this.userInput,
                    is_preview: isPreview,
                })
                .then(async resp => {
                    const attemptId = resp.data.data.id;

                    const { courseId, activityId } = this.$route.params;

                    if (!isPreview) {
                        this.syncActivitySubmissionCount();
                    }

                    await this.$store
                        .dispatch('QuizAttempt/launchQuiz', {
                            attemptId,
                            payload: {
                                activityId,
                                quizActivityId: this.quizActivityId,
                                courseId,
                                quizVersionId: this.quizId,
                                id: this.quizId,
                                preview: this.preview,
                            },
                            type: this.type,
                            router: this.$router,
                        })
                        .catch(error => {
                            this.toggleLoading();

                            throw error;
                        });

                    loading.close();

                    this.$emit('close', { started: !isPreview });
                })
                .catch(e => {
                    loading.close();

                    // Show user-friendly error message
                    let errorMessage = 'An error occurred while starting the quiz.';

                    if (e.response && e.response.data) {
                        if (e.response.data.message) {
                            errorMessage = e.response.data.message;
                        } else if (e.response.data.error) {
                            errorMessage = e.response.data.error;
                        }
                    }

                    this.$edverumToast.open({
                        duration: 5000,
                        message: errorMessage,
                        type: 'is-danger',
                    });

                    // Don't close the modal so user can try again or see the error
                    // this.$emit('close');
                });
        },
    },
};
</script>
